/**
 * Fare Calendar Thunks
 *
 * Async thunks for fare calendar operations
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { FareCalendarTemplate } from '@contracts';
import {
  fetchFareCalendar as fetchFareCalendarApi,
  FetchFareCalendarError,
  type FareCalendarCoreRequest,
  type FareCalendarItineraryLeg,
  type CabinClass,
  type TripType,
} from '../api/fareCalendarApi';
import type { RootState } from '@/store/store';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';
import { logger } from '@/shared/utils/logger';
import { setActiveTemplate } from '@/features/chat/redux/slices/templateSlice';

// ============================================
// Types
// ============================================

/** Thunk params for fetching fare calendar */
export interface FetchFareCalendarThunkParams {
  /** Core request with itinerary details */
  coreRequest: FareCalendarCoreRequest;
  /** Optional query text */
  query?: string;
}

// Re-export types for consumers
export type { FareCalendarCoreRequest, FareCalendarItineraryLeg, CabinClass, TripType };

// ============================================
// Thunks
// ============================================

/**
 * Fetch fare calendar data
 * Automatically includes sessionId and userId from session utils
 *
 * @param params - Request parameters with coreRequest
 * @returns Promise<FareCalendarTemplateData>
 */
export const fetchFareCalendarThunk = createAsyncThunk<
  FareCalendarTemplate,
  FetchFareCalendarThunkParams,
  { state: RootState; rejectValue: string }
>('flights/fetchFareCalendar', async (params, { rejectWithValue, dispatch }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();

    logger.info('[FareCalendarThunk] Fetching fare calendar:', {
      tripType: params.coreRequest.tripType,
      itinerary: params.coreRequest.itinerary,
      cabinClass: params.coreRequest.cabinClass,
      sessionId,
    });

    const data = await fetchFareCalendarApi({
      query: params.query ?? 'Show fare calendar',
      sessionId,
      userId,
      coreRequest: params.coreRequest,
    });

    // Dispatch to template slice for UI rendering
    dispatch(
      setActiveTemplate({
        template: data.activeTemplate,
        data: data.templateData,
      }),
    );

    return data;
  } catch (error) {
    logger.error('[FareCalendarThunk] Failed to fetch fare calendar:', error);
    if (error instanceof FetchFareCalendarError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch fare calendar');
  }
});

// ============================================
// Helper Functions for Consumers
// ============================================

/**
 * Create a core request for one-way trip
 *
 * @example
 * const request = createOneWayRequest('DEL', 'BLR', '20260225');
 */
export function createOneWayRequest(
  from: string,
  to: string,
  date: string,
  options?: {
    cabinClass?: CabinClass;
    numAdults?: number;
    numChildren?: number;
    numInfant?: number;
  },
): FareCalendarCoreRequest {
  return {
    cabinClass: options?.cabinClass ?? 'E',
    itinerary: [{ from, to, date }],
    numAdults: options?.numAdults ?? 1,
    numChildren: options?.numChildren ?? 0,
    numInfant: options?.numInfant ?? 0,
    tripType: 'OW',
  };
}

/**
 * Create a core request for round-trip
 *
 * @example
 * const request = createRoundTripRequest('DEL', 'BLR', '20260225', '20260227');
 */
export function createRoundTripRequest(
  from: string,
  to: string,
  departureDate: string,
  returnDate: string,
  options?: {
    cabinClass?: CabinClass;
    numAdults?: number;
    numChildren?: number;
    numInfant?: number;
  },
): FareCalendarCoreRequest {
  return {
    cabinClass: options?.cabinClass ?? 'E',
    itinerary: [
      { from, to, date: departureDate },
      { from: to, to: from, date: returnDate },
    ],
    numAdults: options?.numAdults ?? 1,
    numChildren: options?.numChildren ?? 0,
    numInfant: options?.numInfant ?? 0,
    tripType: 'RT',
  };
}

/**
 * Create a core request for multi-city trip
 *
 * @example
 * const request = createMultiCityRequest([
 *   { from: 'DEL', to: 'BLR', date: '20260225' },
 *   { from: 'BLR', to: 'HYD', date: '20260227' },
 *   { from: 'HYD', to: 'DEL', date: '20260301' },
 * ]);
 */
export function createMultiCityRequest(
  itinerary: FareCalendarItineraryLeg[],
  options?: {
    cabinClass?: CabinClass;
    numAdults?: number;
    numChildren?: number;
    numInfant?: number;
  },
): FareCalendarCoreRequest {
  return {
    cabinClass: options?.cabinClass ?? 'E',
    itinerary,
    numAdults: options?.numAdults ?? 1,
    numChildren: options?.numChildren ?? 0,
    numInfant: options?.numInfant ?? 0,
    tripType: 'MC',
  };
}
